import RangePicker from '@home/components/RangePicker';
import BubbleCommon, { realtimeStreamingTimeRange } from '@op/components/BubbleCommon';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import CustomSingleSelect from '@op/components/FilterCommon/components/CustomSingleSelect';
import {
  dayToDaily,
  dailyToDay,
  dailyFormat,
  defaultRangePickerValue,
  newOpinionOptionsMap,
} from '@op/components/FilterCommon/constants/date';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_POINT } from '@op/operationLog/buriedPoint';
import { DBO_TM } from '@op/utils/format';
import { formatTimeToServer } from '@op/utils/time';
import moment, { Moment } from 'moment';
import React, { useMemo, useState } from 'react';
import { SearchBox } from 'tea-component';

export type FilterValueType = {
  start_time: string;
  end_time: string;
  date_type: string;
  kolType: string;
  search_text: string;
  status: string;
  is_today: boolean;
};

export default function Filter({
  filter,
  onChange,
  download,
  enableDateRange = false,
  isGST
}: {
  filter: FilterValueType;
  onChange: (value: Partial<FilterValueType>) => void;
  download: () => void;
  enableDateRange?: boolean;
  isGST?:boolean;
}) {
  const { isEn, formatMessage, formatOptionsMessage } = useLanguage();

  const [localDateType, setLocalDateType] = useState<string>(
    () => dailyToDay[filter.date_type] || 'date',
  );

  const onChangeRangePicker = (value: Array<Moment>, type: string) => {
    const serverDateType = dayToDaily[type] || 'daily';
    setLocalDateType(type);

    const hasTime = value.every(Boolean) && value.length > 0;
    const rangePickerValues = defaultRangePickerValue();
    const newValue = hasTime
      ? value
      : rangePickerValues[dailyToDay[serverDateType] || type || 'date'];

    const timeStrings = newValue.map((item: Moment | string) =>
      (typeof item === 'string' ? moment(item) : item).format(
        dailyFormat[serverDateType],
      ),
    ) as [string, string];
    const [startTime, endTime] = formatTimeToServer(
      timeStrings,
      serverDateType as any,
    );

    const data = {
      start_time: startTime,
      end_time: endTime,
      date_type: serverDateType,
    };

    const isToday = DBO_TM.isToday(data);
    onChange({
      ...data,
      status: isToday ? 'now' : 'day',
      is_today: isToday,
    });
    DBO_POINT.btn('FilterTime', undefined, JSON.stringify(data));
  };

  // Old single-day picker onChange
  const onChangeSingleDay = (value: [Moment, Moment]) => {
    const [start_time, end_time] = value;
    const data = {
      start_time: start_time
        .startOf('day')
        .startOf('hour')
        ?.format('YYYY-MM-DD HH:mm:ss'),
      end_time: end_time
        .endOf('day')
        .endOf('hour')
        ?.format('YYYY-MM-DD HH:mm:ss'),
    };
    const isToday = DBO_TM.isToday(data);
    onChange({ ...data, date_type: 'daily', status: isToday ? 'now' : 'day' });
    DBO_POINT.btn('FilterTime', undefined, JSON.stringify(data));
  };

  const rangePickerValue = useMemo(() => {
    const { start_time, end_time } = filter;
    return [start_time, end_time].map((time: string) => moment(time));
  }, [filter]);

  function searchTextChange(text: string) {
    onChange({ search_text: text });
    DBO_POINT.btn('Search', undefined, text);
  }

  const restRangePickerProps = useMemo(() => {
    if (enableDateRange) {
      return {
        onChange: onChangeRangePicker,
        typeList: ['date', 'week', 'month'],
        type: localDateType,
        defaultValue: defaultRangePickerValue(),
        optionsMap: newOpinionOptionsMap,
        showSelectType: true,
        showShortcut: true,
        selectClassName: 'dbo-realtime-streaming-datepicker__select',
        rangePickerClassName: 'dbo-realtime-streaming-datepicker__range-picker'
      };
    }
    return {
      onChange: onChangeSingleDay,
      typeList: ['1day'],
      type: '1day',
      defaultValue: { '1day': [DBO_TM.utc(), DBO_TM.utc()] },
      optionsMap: {
        '1day': { value: '1day', text: 'Day', text_cn: '天' },
      },
      showSelectType: false,
    };
  }, [enableDateRange, localDateType]);

  return (
    <FilterBoxCommon
      left={[
        {
          key: 'distance',
          element: (
            <BubbleCommon tipsMap={realtimeStreamingTimeRange} placement='top' showIcon={false}>
              <RangePicker
                autoStartDay={false}
                value={rangePickerValue}
                disables={false}
                language={isEn ? 'en' : 'cn'}
                startOfTime={'2001-01-01'}
                hourLeftSide={false}
                {...restRangePickerProps}
              />
            </BubbleCommon>
          ),
        },
        {
          key: 'metrics',
          element: (
            <CustomSingleSelect
              placeholderText={formatMessage('Streamer Type')}
              selectValueInfo={[
                filter.kolType,
                (value) => {
                  onChange({ kolType: value });
                  DBO_POINT.btn('FilterStreamer', undefined, value);
                },
              ]}
              options={formatOptionsMessage([
                { text: 'All', value: 'all' },
                { text: 'Featured', value: 'featured' },
                { text: 'Others', value: 'others' },
              ])}
            />
          ),
          show: !isGST
        },
        {
          key: 'pos',
          element: (
            <CustomSingleSelect
              placeholderText={formatMessage('Streaming Status')}
              selectValueInfo={[
                filter.status,
                (value) => {
                  onChange({ status: value });
                  DBO_POINT.btn('FilterStatus', undefined, value);
                },
              ]}
              options={formatOptionsMessage([
                {
                  text: 'Live Now',
                  value: 'now',
                  disabled: !DBO_TM.isToday(filter),
                },
                { text: 'Live Today', value: 'day' },
              ])}
            />
          ),
        },
      ]}
      right={[
        {
          key: 'search',
          element: (
            <SearchBox
              defaultValue={filter.search_text}
              placeholder={formatMessage('Search streamer/title/URL')}
              onSearch={(value) => searchTextChange(value!)}
              onClear={() => onChange({ search_text: '' })}
              onBlur={(event) => {
                const text = event?.target?.value || '';
                if (text !== filter.search_text) {
                  searchTextChange(text);
                }
              }}
            />
          ),
        },
        {
          key: 'download',
          event: {
            onClick: () => {
              download();
              DBO_POINT.btn('Download', undefined, undefined);
            },
          },
        },
      ]}
      onChange={() => { }}
    ></FilterBoxCommon>
  );
}
